import { useState, useEffect } from "react";
import { cn } from "@util/tailwind";
import { Phone, CheckCircle, XCircle, PhoneOff, Clock, UserPlus, UserMinus, Play, Pause, Settings, Activity, Loader2 } from "lucide-react";
import type { ActivityLogEntry, ActivityEventType } from "@type/agent";
import { getCampaignActivityLog } from "@service/agent";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { formatMYTRelative } from "@util/date";

interface ActivityLogProps {
    campaignId: string;
}

type FilterType = "all" | "calls" | "system";

const eventTypeConfig: Record<ActivityEventType, { icon: typeof Phone; bgColor: string; iconColor: string }> = {
    call_initiated: { icon: Phone, bgColor: "bg-blue-50", iconColor: "text-blue-500" },
    call_completed: { icon: CheckCircle, bgColor: "bg-green-50", iconColor: "text-green-500" },
    call_failed: { icon: XCircle, bgColor: "bg-red-50", iconColor: "text-red-500" },
    call_no_answer: { icon: PhoneOff, bgColor: "bg-amber-50", iconColor: "text-amber-500" },
    retry_scheduled: { icon: Clock, bgColor: "bg-purple-50", iconColor: "text-purple-500" },
    contact_added: { icon: UserPlus, bgColor: "bg-blue-50", iconColor: "text-blue-500" },
    contact_removed: { icon: UserMinus, bgColor: "bg-gray-100", iconColor: "text-gray-500" },
    campaign_activated: { icon: Play, bgColor: "bg-green-50", iconColor: "text-green-500" },
    campaign_paused: { icon: Pause, bgColor: "bg-amber-50", iconColor: "text-amber-500" },
    schedule_updated: { icon: Settings, bgColor: "bg-gray-100", iconColor: "text-gray-500" },
    settings_changed: { icon: Settings, bgColor: "bg-gray-100", iconColor: "text-gray-500" },
};

const callEventTypes: ActivityEventType[] = ["call_initiated", "call_completed", "call_failed", "call_no_answer", "retry_scheduled"];

function formatTimestamp(date: Date | string): string {
    return formatMYTRelative(date);
}

export function ActivityLog({ campaignId }: ActivityLogProps) {
    const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<FilterType>("all");

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const result = await getCampaignActivityLog(campaignId);
                setEntries(result.entries);
                setTotal(result.total);
            } catch {
                setEntries([]);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [campaignId]);

    const filteredEntries = entries
        .filter((entry) => {
            if (filter === "calls") return callEventTypes.includes(entry.eventType);
            if (filter === "system") return !callEventTypes.includes(entry.eventType);
            return true;
        })
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="flex-row items-center justify-between">
                    <CardTitle>Activity Log</CardTitle>
                    <span className="text-sm text-gray-500">{total} events</span>
                </CardHeader>
                <CardContent>
                    <div className="flex gap-2 mb-4">
                        {(["all", "calls", "system"] as FilterType[]).map((btn) => (
                            <button key={btn} onClick={() => setFilter(btn)} className={cn("px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize", filter === btn ? "bg-blue-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 hover:bg-gray-200")}>
                                {btn}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    ) : filteredEntries.length > 0 ? (
                        <div className="space-y-0">
                            {filteredEntries.map((entry) => {
                                const config = eventTypeConfig[entry.eventType] || { icon: Activity, bgColor: "bg-gray-100", iconColor: "text-gray-500" };
                                const EventIcon = config.icon;
                                return (
                                    <div key={entry.id} className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
                                        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0", config.bgColor)}>
                                            <EventIcon className={cn("w-4 h-4", config.iconColor)} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-white">{entry.message}</p>
                                            {entry.details && <p className="text-xs text-gray-500 mt-0.5">{entry.details}</p>}
                                            {entry.contactPhone && <p className="text-xs text-gray-400 mt-0.5">{entry.contactPhone}</p>}
                                        </div>
                                        <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{formatTimestamp(entry.timestamp)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Activity className="w-10 h-10 mb-3" />
                            <p className="text-sm font-medium">No activity recorded yet.</p>
                            <p className="text-xs mt-1">Activity will appear here once the campaign starts.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
